// @flow

import { types as t } from '../validation/runtime-checks/index.js';
import { Text } from '../Text.js';
import { List } from '../List.js';
import Image from '../Image.js';
import BoxAndWhiskerPlot from '../BoxAndWhiskerPlot.js';
import { CartesianPlane } from '../CartesianPlane.js';
import { NumberLine } from '../NumberLine.js';
import { StemAndLeafPlot } from '../StemAndLeafPlot.js';
import TabularData from '../TabularData.js';
import { TwoColumnProof } from '../TwoColumnProof.js';
import { AlgebraicWorking } from '../AlgebraicWorking.js';
import DraftImage from '../DraftImage.js';
import { RSLExplainedEquation } from './common-components/RSLExplainedEquation.js';
import { RSLGlossaryTermsList } from './common-components/RSLTermDefinition.js';
import { RSLAnnotatedFigure } from './common-components/RSLAnnotatedFigure.js';
import { RSLFigureGrid } from './common-components/RSLFigureGrid.js';
import { RSLMultipleChoiceTextOptions } from './common-components/RSLMultipleChoiceTextOptions.js';
import { RSLMultipleChoiceImageOptions } from './common-components/RSLMultipleChoiceImageOptions.js';
import { RSLTechnologyToolScreenshot } from './common-components/RSLTechnologyToolScreenshot.js';
import { PseudoCodeBlock } from '../PseudoCodeBlock.js';

export type SharedRSLChildren =
  | React$Element<typeof Text>
  | React$Element<typeof List>
  | React$Element<typeof Image>
  | React$Element<typeof DraftImage>
  | React$Element<typeof BoxAndWhiskerPlot>
  | React$Element<typeof CartesianPlane>
  | React$Element<typeof NumberLine>
  | React$Element<typeof StemAndLeafPlot>
  | React$Element<typeof TabularData>
  | React$Element<typeof TwoColumnProof>
  | React$Element<typeof AlgebraicWorking>
  | React$Element<typeof PseudoCodeBlock>
  // RSL-scoped components
  | React$Element<typeof RSLExplainedEquation>
  | React$Element<typeof RSLGlossaryTermsList>
  | React$Element<typeof RSLAnnotatedFigure>
  | React$Element<typeof RSLFigureGrid>;

export const sharedRSLChildrenElementTypes = [
  // Primitives
  t.elementType(Text),
  t.elementType(List),
  t.elementType(Image),
  t.elementType(DraftImage),
  t.elementType(BoxAndWhiskerPlot),
  t.elementType(CartesianPlane),
  t.elementType(NumberLine),
  t.elementType(StemAndLeafPlot),
  t.elementType(TabularData),
  t.elementType(TwoColumnProof),
  t.elementType(AlgebraicWorking),
  t.elementType(PseudoCodeBlock),

  // RSL-scoped components
  t.elementType(RSLExplainedEquation),
  t.elementType(RSLGlossaryTermsList),
  t.elementType(RSLAnnotatedFigure),
  t.elementType(RSLFigureGrid),
];

// Used for both Question and Subquestion statement
export type SharedStatementChildren =
  | React$Element<typeof Text>
  | React$Element<typeof List>
  | React$Element<typeof Image>
  | React$Element<typeof TabularData>
  | React$Element<typeof BoxAndWhiskerPlot>
  | React$Element<typeof CartesianPlane>
  | React$Element<typeof NumberLine>
  | React$Element<typeof StemAndLeafPlot>
  | React$Element<typeof RSLAnnotatedFigure>
  | React$Element<typeof AlgebraicWorking>
  | React$Element<typeof TwoColumnProof>
  | React$Element<typeof RSLMultipleChoiceTextOptions>
  | React$Element<typeof RSLMultipleChoiceImageOptions>
  | React$Element<typeof PseudoCodeBlock>;

export const sharedStatementChildrenChecker = t.childrenArray(
  t.unionOfTypes(
    t.elementType(Text),
    t.elementType(List),
    t.elementType(Image),
    t.elementType(DraftImage),
    t.elementType(TabularData),
    t.elementType(BoxAndWhiskerPlot),
    t.elementType(CartesianPlane),
    t.elementType(NumberLine),
    t.elementType(StemAndLeafPlot),
    t.elementType(RSLAnnotatedFigure),
    t.elementType(AlgebraicWorking),
    t.elementType(TwoColumnProof),
    t.elementType(RSLMultipleChoiceTextOptions),
    t.elementType(RSLMultipleChoiceImageOptions),
    t.elementType(PseudoCodeBlock),
  ),
);

export type SolutionChildren =
  | React$Element<typeof Text>
  | React$Element<typeof List>
  | React$Element<typeof Image>
  | React$Element<typeof DraftImage>
  | React$Element<typeof RSLTechnologyToolScreenshot>
  | React$Element<typeof TabularData>
  | React$Element<typeof BoxAndWhiskerPlot>
  | React$Element<typeof CartesianPlane>
  | React$Element<typeof NumberLine>
  | React$Element<typeof StemAndLeafPlot>
  | React$Element<typeof RSLAnnotatedFigure>
  | React$Element<typeof AlgebraicWorking>
  | React$Element<typeof TwoColumnProof>
  | React$Element<typeof PseudoCodeBlock>;

export const solutionChildrenChecker = t.childrenArray(
  t.unionOfTypes(
    t.elementType(Text),
    t.elementType(List),
    t.elementType(Image),
    t.elementType(DraftImage),
    t.elementType(RSLTechnologyToolScreenshot),
    t.elementType(TabularData),
    t.elementType(BoxAndWhiskerPlot),
    t.elementType(CartesianPlane),
    t.elementType(NumberLine),
    t.elementType(StemAndLeafPlot),
    t.elementType(RSLAnnotatedFigure),
    t.elementType(AlgebraicWorking),
    t.elementType(TwoColumnProof),
    t.elementType(PseudoCodeBlock),
  ),
);
